import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ColorThemeService, parseColorTheme } from 'lib-trend-core';
import { isSafeChatAppPath } from 'lib-trend-core/chat';
import { ChatAppSessionService } from '../../core/chat-app-session.service';

/**
 * Handoff landing — SPEC-2026-09-internal-chat-pwa (T-33 · RF-05 · RF-06 · RF-07).
 *
 * The product opens `/handoff#code=…&to=/c/…`. The code travels in the fragment, which
 * never reaches a server log or a `Referer`, and is stripped from the address BEFORE any
 * request, so it never lingers in the history either. Then:
 * - same person, same company as the stored session → straight to the destination;
 * - no session, or another person/company → the previous person's push registration is
 *   removed, the new session is kept, and a full load shows nothing of the previous one;
 * - the code is refused (used, expired, unknown — the API never says which) → the
 *   existing session continues, or sign-in opens with the destination preserved.
 */
@Component({
  selector: 'chat-app-handoff',
  templateUrl: './chat-app-handoff.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ChatAppHandoffComponent implements OnInit {

  failed = false;

  destination = '/';

  constructor(
    private readonly session: ChatAppSessionService,
    private readonly colorTheme: ColorThemeService,
  ) { }

  async ngOnInit(): Promise<void> {
    const fragment = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const code = fragment.get('code');
    const to = fragment.get('to');
    this.destination = isSafeChatAppPath(to) ? to : '/';

    // The product's theme rides along (DEV-2062): the chat app opens — and remembers — the same one.
    const theme = parseColorTheme(fragment.get('theme'));
    if (theme) this.colorTheme.apply(theme);

    // Nothing below may run with the code still in the address bar.
    window.history.replaceState(window.history.state, '', '/handoff');

    if (!code) {
      this.continueWithoutCode();
      return;
    }

    try {
      const exchanged = await this.session.exchangeHandoffCode(code);
      if (this.session.isSameIdentity(exchanged)) {
        window.location.replace(this.destination);
        return;
      }
      await this.session.adopt(exchanged);
      window.location.replace(this.destination);
    } catch {
      this.continueWithoutCode();
    }
  }

  private continueWithoutCode(): void {
    if (this.session.isAuthenticated()) {
      window.location.replace(this.destination);
      return;
    }
    // One localized message, the same for every refusal, then sign-in keeps the destination.
    this.failed = true;
  }

  get signInPath(): string {
    return this.session.signInPath(null, this.destination);
  }
}
